import React,{useState,useEffect,useRef} from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import {Line} from 'react-chartjs-2';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiCalculator,FiDownload,FiInfo,FiTrendingDown,FiDollarSign,FiCalendar}=FiIcons;

// ChartJS registrieren
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function MortgageCalculator() {
  // --- State für Eingabewerte ---
  const [inputs,setInputs]=useState({
    amount: 150000,
    interestRate: 3.5,
    repaymentRate: 2.0,
    years: 10,
    unscheduled: 0
  });

  // --- State für Ergebnisse ---
  const [results,setResults]=useState({
    monthlyRate: 0,
    totalInterest: 0,
    endBalance: 0,
    yearlySchedule: []
  });

  // --- Berechnung ---
  useEffect(()=> {
    calculate();
  },[inputs]);

  const handleInputChange=(e)=> {
    const {name,value}=e.target;
    setInputs(prev=> ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const calculate=()=> {
    const {amount,interestRate,repaymentRate,years,unscheduled}=inputs;

    // Grundlegende Formeln
    const monthlyInterestRate=interestRate / 100 / 12;
    // Annuität = Zinsanteil + Tilgungsanteil (Monatlich)
    // Rate = Kreditbetrag * (Zins% + Tilgung%) / 12
    const monthlyRateRaw=(amount * ((interestRate + repaymentRate) / 100)) / 12;
    
    // Runden für saubere Anzeige
    const monthlyRate=Math.round(monthlyRateRaw * 100) / 100;

    let balance=amount;
    let totalInterest=0;
    let schedule=[];
    let currentYearData={year: 1,interest: 0,repayment: 0,rate: 0,balance: 0};

    // Monatliche Schleife für die gesamte Laufzeit
    const totalMonths=years * 12;

    for (let m=1;m <=totalMonths;m++) {
      // Zinsen für diesen Monat
      const interest=balance * monthlyInterestRate;
      
      // Tilgungsanteil = Rate - Zins
      let repayment=monthlyRate - interest;

      // Wenn Restschuld kleiner als Rate,Rate anpassen
      if (balance < repayment) {
        repayment=balance;
      }

      // Restschuld verringern
      balance -= repayment;
      totalInterest +=interest;

      // Daten für Jahr sammeln
      currentYearData.interest +=interest;
      currentYearData.repayment +=repayment;
      currentYearData.rate +=monthlyRate; // Vereinfacht,die letzte Rate könnte kleiner sein

      // Sondertilgung am Jahresende (Monat 12,24,36...)
      if (m % 12===0) {
        currentYearData.balance=balance; // Zwischenstand vor Sondertilgung
        
        if (unscheduled > 0 && balance > 0) {
          const sondertilgung=Math.min(balance,unscheduled);
          balance -= sondertilgung;
          currentYearData.repayment +=sondertilgung;
          currentYearData.balance=balance; // Endstand nach Sondertilgung
        }

        // Jahr abschließen und speichern
        schedule.push({...currentYearData});
        
        // Neues Jahr vorbereiten
        currentYearData={
          year: (m / 12) + 1,
          interest: 0,
          repayment: 0,
          rate: 0,
          balance: 0
        };
      }

      if (balance <=0) break;
    }

    setResults({
      monthlyRate,
      totalInterest,
      endBalance: Math.max(0,balance),
      yearlySchedule: schedule
    });
  };

  // --- CSV Export ---
  const exportCSV=()=> {
    const headers=["Jahr","Rate (€)","Zinsen (€)","Tilgung (€)","Restschuld (€)"];
    const rows=results.yearlySchedule.map(row=> [
      row.year,
      row.rate.toFixed(2).replace('.',','),
      row.interest.toFixed(2).replace('.',','),
      row.repayment.toFixed(2).replace('.',','),
      row.balance.toFixed(2).replace('.',',')
    ]);

    const csvContent=[
      headers.join(';'),
      ...rows.map(e=> e.join(';'))
    ].join('\n');

    const blob=new Blob([csvContent],{type: 'text/csv;charset=utf-8;'});
    const link=document.createElement("a");
    const url=URL.createObjectURL(blob);
    link.setAttribute("href",url);
    link.setAttribute("download","tilgungsplan_mk_ausbau.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- Chart Data ---
  const chartData={
    labels: results.yearlySchedule.map(d=> `Jahr ${d.year}`),
    datasets: [
      {
        label: 'Restschuld (€)',
        data: results.yearlySchedule.map(d=> d.balance),
        borderColor: '#2d5f5f',
        backgroundColor: 'rgba(45,95,95,0.1)',
        fill: true,
        tension: 0.3,
      }
    ],
  };

  const chartOptions={
    responsive: true,
    plugins: {
      legend: {position: 'top'},
      title: {display: false},
      tooltip: {
        callbacks: {
          label: (context)=> `${context.parsed.y.toLocaleString('de-DE',{style: 'currency',currency: 'EUR'})}`
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: (value)=> value.toLocaleString('de-DE') + ' €'
        }
      }
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden my-12" id="tilgungsrechner">
      <div className="bg-mk-teal-600 p-6 text-white flex items-center justify-between">
        <h3 className="text-xl font-bold flex items-center">
          <SafeIcon icon={FiCalculator} className="mr-3 text-mk-teal-200" />
          Immobilien-Tilgungsrechner
        </h3>
        <span className="text-xs bg-mk-teal-700 px-3 py-1 rounded-full text-mk-teal-100 hidden sm:inline-block">Simulation</span>
      </div>

      <div className="p-6 lg:p-8">
        {/* Eingabefelder */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="input-group">
            <label className="block text-sm font-bold text-gray-700 mb-2">Darlehensbetrag</label>
            <div className="relative">
              <input
                type="number"
                name="amount"
                value={inputs.amount}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-teal-500 focus:border-mk-teal-500 transition-all font-mono"
              />
              <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          <div className="input-group">
            <label className="block text-sm font-bold text-gray-700 mb-2">Sollzins (% p.a.)</label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                name="interestRate"
                value={inputs.interestRate}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-teal-500 focus:border-mk-teal-500 transition-all font-mono"
              />
              <SafeIcon icon={FiTrendingDown} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          <div className="input-group">
            <label className="block text-sm font-bold text-gray-700 mb-2">Anfängliche Tilgung (% p.a.)</label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                name="repaymentRate"
                value={inputs.repaymentRate}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-teal-500 focus:border-mk-teal-500 transition-all font-mono"
              />
              <SafeIcon icon={FiTrendingDown} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          <div className="input-group">
            <label className="block text-sm font-bold text-gray-700 mb-2">Zinsbindung / Laufzeit (Jahre)</label>
            <div className="relative">
              <input
                type="number"
                name="years"
                value={inputs.years}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-teal-500 focus:border-mk-teal-500 transition-all font-mono"
              />
              <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          <div className="input-group">
            <label className="block text-sm font-bold text-gray-700 mb-2">Sondertilgung (jährlich €)</label>
            <div className="relative">
              <input
                type="number"
                name="unscheduled"
                value={inputs.unscheduled}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-teal-500 focus:border-mk-teal-500 transition-all font-mono"
              />
              <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Ergebnisse Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-mk-teal-50 p-4 rounded-xl border border-mk-teal-100 text-center">
            <p className="text-sm text-mk-teal-700 font-medium mb-1">Monatliche Rate</p>
            <p className="text-2xl font-bold text-mk-teal-900">
              {results.monthlyRate.toLocaleString('de-DE',{minimumFractionDigits: 2,maximumFractionDigits: 2})} €
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center">
            <p className="text-sm text-gray-600 font-medium mb-1">Gezahlte Zinsen ({inputs.years} Jahre)</p>
            <p className="text-xl font-bold text-gray-800">
              {results.totalInterest.toLocaleString('de-DE',{minimumFractionDigits: 2,maximumFractionDigits: 2})} €
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center">
            <p className="text-sm text-gray-600 font-medium mb-1">Restschuld nach Laufzeit</p>
            <p className={`text-xl font-bold ${results.endBalance > 0 ? 'text-mk-red-600' : 'text-green-600'}`}>
              {results.endBalance.toLocaleString('de-DE',{minimumFractionDigits: 2,maximumFractionDigits: 2})} €
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="mb-8 h-64 w-full">
          <Line options={chartOptions} data={chartData} />
        </div>

        {/* Tabelle */}
        <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
          <table className="min-w-full text-sm text-right">
            <thead className="bg-gray-100 text-gray-700 font-bold">
              <tr>
                <th className="px-4 py-3 text-left">Jahr</th>
                <th className="px-4 py-3">Rate</th>
                <th className="px-4 py-3">Zinsen</th>
                <th className="px-4 py-3">Tilgung</th>
                <th className="px-4 py-3">Restschuld</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {results.yearlySchedule.map((row)=> (
                <tr key={row.year} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2 text-left font-bold text-gray-900">{row.year}</td>
                  <td className="px-4 py-2">{row.rate.toLocaleString('de-DE',{minimumFractionDigits: 2,maximumFractionDigits: 2})} €</td>
                  <td className="px-4 py-2 text-mk-red-600">{row.interest.toLocaleString('de-DE',{minimumFractionDigits: 2,maximumFractionDigits: 2})} €</td>
                  <td className="px-4 py-2 text-green-600">{row.repayment.toLocaleString('de-DE',{minimumFractionDigits: 2,maximumFractionDigits: 2})} €</td>
                  <td className="px-4 py-2 font-bold">{row.balance.toLocaleString('de-DE',{minimumFractionDigits: 2,maximumFractionDigits: 2})} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Actions & Disclaimer */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <button
            onClick={exportCSV}
            className="flex items-center space-x-2 text-mk-teal-600 hover:text-mk-teal-700 font-bold px-4 py-2 rounded-lg hover:bg-mk-teal-50 transition-colors border border-mk-teal-200"
          >
            <SafeIcon icon={FiDownload} />
            <span>Als CSV exportieren</span>
          </button>

          {/* Hover Disclaimer */}
          <div className="relative group">
            <div className="flex items-center text-gray-400 hover:text-gray-600 cursor-help text-sm">
              <SafeIcon icon={FiInfo} className="mr-1" />
              <span>Haftungsausschluss anzeigen</span>
            </div>
            
            {/* Tooltip / Layer */}
            <div className="absolute bottom-full right-0 mb-2 w-72 sm:w-96 bg-gray-900 text-white text-xs p-4 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
              <p className="font-bold mb-2 border-b border-gray-700 pb-1">Haftungsausschluss</p>
              <p className="mb-2">
                Dieser Tilgungsrechner stellt lediglich ein unverbindliches Informations- und Berechnungstool dar. Die dargestellten Ergebnisse basieren ausschließlich auf den vom Nutzer eingegebenen Daten sowie vereinfachten Berechnungsmodellen. Trotz sorgfältiger Programmierung und Prüfung wird keine Gewähr für die Richtigkeit,Vollständigkeit oder Aktualität der Berechnungsergebnisse übernommen.
              </p>
              <p className="mb-2">
                Die dargestellten Ergebnisse stellen weder eine Finanzierungsberatung noch ein verbindliches Angebot zum Abschluss eines Kreditvertrages dar. Maßgeblich sind ausschließlich die individuell von Kreditinstituten angebotenen Konditionen sowie die persönlichen finanziellen Voraussetzungen des Antragstellers.
              </p>
              <p>
                Jegliche Haftung für Entscheidungen,die auf Grundlage der dargestellten Berechnungsergebnisse getroffen werden,ist ausgeschlossen.
              </p>
              <div className="absolute bottom-[-6px] right-6 w-3 h-3 bg-gray-900 transform rotate-45"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MortgageCalculator;